import { useEffect } from "react"
import { usePage } from "@inertiajs/react"
import { ChevronLeft, ChevronRight, X } from "lucide-react"
import type { PageProps } from "@/types/inertia"

export interface LightboxPhoto {
  id: number
  full_url: string
  alt_text: string
}

interface Props {
  photos: LightboxPhoto[]
  /** Index of the open photo, or null when the lightbox is closed. */
  index: number | null
  onChange: (index: number | null) => void
}

// The full-screen photo viewer both galleries share: the whole page's photos in
// one list, whichever section the visitor opened it from, so the arrows never
// dead-end at a room boundary. Closed when `index` is null.
export default function PhotoLightbox({ photos, index, onChange }: Props) {
  const { translations } = usePage<PageProps>().props
  const t = translations.sanctuary_pages.gallery

  const close = () => onChange(null)
  const step = (by: number) =>
    onChange(index === null ? null : (index + by + photos.length) % photos.length)

  useEffect(() => {
    if (index === null) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape")     close()
      if (e.key === "ArrowRight") step(1)
      if (e.key === "ArrowLeft")  step(-1)
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [index, photos.length])

  useEffect(() => {
    document.body.style.overflow = index !== null ? "hidden" : ""
    return () => { document.body.style.overflow = "" }
  }, [index])

  if (index === null) return null
  const photo = photos[index]
  if (!photo) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
      onClick={close}
    >
      <img
        src={photo.full_url}
        alt={photo.alt_text}
        className="max-h-[90vh] max-w-[85vw] object-contain"
        onClick={e => e.stopPropagation()}
      />

      <button
        onClick={close}
        className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white backdrop-blur-sm transition-colors hover:bg-white/20"
        aria-label={t.close}
      >
        <X className="h-5 w-5" />
      </button>

      {photos.length > 1 && (
        <>
          <button
            onClick={e => { e.stopPropagation(); step(-1) }}
            className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white backdrop-blur-sm transition-colors hover:bg-white/20"
            aria-label={t.previous_photo}
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={e => { e.stopPropagation(); step(1) }}
            className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white backdrop-blur-sm transition-colors hover:bg-white/20"
            aria-label={t.next_photo}
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}

      <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-sm text-white/50">
        {index + 1} / {photos.length}
      </p>
    </div>
  )
}
